/**
 * Delay before third-party work (GTM / WebEngage / VWO / modals / widgets)
 * is loaded. No interaction listeners — a plain timer after page load.
 *
 * NOTE for lab scores: PageSpeed's mobile trace usually runs 10-20s, so at
 * 3s the scripts load inside the trace and are counted in the third-party /
 * TBT audits. Raise to ~10s+ if the priority is the lab score.
 */
const DELAY_TIMEOUT_MS = 5000;

/**
 * When the timer fires we still wait for an idle slot before running, so the
 * script parse/eval can never land in the middle of a user interaction (INP).
 * If the browser stays busy, this caps how long idle is awaited.
 */
const IDLE_SLOT_MAX_WAIT_MS = 2000;

export type DeferThirdPartyOptions = {
  /**
   * Extra delay after the gate opens, used to stagger non-lead work so GTM /
   * WebEngage / modals are not competing with chatbot etc. on the same tick.
   * Lead scripts must keep delayMs = 0.
   */
  delayMs?: number;
};

/**
 * Schedules third-party work after a fixed delay. INP-safe: once the delay
 * elapses, the load runs in a requestIdleCallback slot (main thread free),
 * never inside an interaction's task. Optional delayMs staggers non-lead
 * mounts after the gate so everything doesn't parse/eval on one tick.
 */
export function deferThirdPartyScript(
  load: () => void,
  options?: DeferThirdPartyOptions,
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const delayMs = options?.delayMs ?? 0;
  let cancelled = false;
  let done = false;
  let timerId: number | undefined;
  let staggerId: number | undefined;
  let idleId: number | undefined;

  const cleanup = () => {
    if (timerId !== undefined) {
      window.clearTimeout(timerId);
      timerId = undefined;
    }
    if (staggerId !== undefined) {
      window.clearTimeout(staggerId);
      staggerId = undefined;
    }
    if (idleId !== undefined && "cancelIdleCallback" in window) {
      (
        window as Window & { cancelIdleCallback: (handle: number) => void }
      ).cancelIdleCallback(idleId);
      idleId = undefined;
    }
  };

  const runLoad = () => {
    if (cancelled || done) return;
    done = true;
    cleanup();
    load();
  };

  const run = () => {
    if (cancelled || done) return;
    if (delayMs > 0) {
      staggerId = window.setTimeout(runLoad, delayMs);
      return;
    }
    runLoad();
  };

  // Delay elapsed -> wait for an idle slot so we never compete with an
  // in-flight interaction on the main thread.
  const onDelayElapsed = () => {
    if (cancelled || done) return;

    const requestIdleCallback = (
      window as Window & {
        requestIdleCallback?: (
          callback: IdleRequestCallback,
          options?: IdleRequestOptions,
        ) => number;
      }
    ).requestIdleCallback;

    if (requestIdleCallback) {
      idleId = requestIdleCallback(run, { timeout: IDLE_SLOT_MAX_WAIT_MS });
    } else {
      run();
    }
  };

  timerId = window.setTimeout(onDelayElapsed, DELAY_TIMEOUT_MS);

  return () => {
    cancelled = true;
    cleanup();
  };
}
